/**
 * useGiftScenarios (CHAP-301) - tax ruling read + saved gift scenarios +
 * save mutation for the fundraising gift calculator.
 *
 * The scenarios list + save are cabinet+ endpoints; the list query hook is
 * only mounted inside the TierGuard'd save panel so sub-cabinet members
 * never fire a 403. The tax-ruling read is any-member.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';
import type {
  GiftInput,
  GiftScenariosResponse,
  SavedGiftScenario,
  TaxRulingResponse,
} from '../../shared/gift-calculator.js';

export function useTaxRuling(slug: string) {
  return useQuery<TaxRulingResponse, Error>({
    queryKey: ['tax-deductibility-ruling', slug],
    queryFn: () => apiGet<TaxRulingResponse>(`/api/c/${slug}/tax-deductibility-ruling`),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGiftScenarios(slug: string) {
  return useQuery<GiftScenariosResponse, Error>({
    queryKey: ['gift-scenarios', slug],
    queryFn: () => apiGet<GiftScenariosResponse>(`/api/c/${slug}/gift-scenarios`),
    enabled: !!slug,
  });
}

export interface SaveGiftScenarioBody extends GiftInput {
  name: string;
}

export function useSaveGiftScenario(slug: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; scenario: SavedGiftScenario }, Error, SaveGiftScenarioBody>({
    mutationFn: (body) => apiPost(`/api/c/${slug}/gift-scenarios`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gift-scenarios', slug] }),
  });
}
